import axios from 'axios';
let httpInstance = axios;
// Allow main.js to set the configured axios instance
export function setHttpInstance(instance) {
    httpInstance = instance;
}
export function getHttpInstance() {
    return httpInstance;
}
const SWR_POLL_MAX_ATTEMPTS = 5;
const SWR_POLL_INTERVAL_MS = 5000;
let apiConfig = {
    api: '',
    configUri: '',
    useCachedEndpoints: true,
    useCache: true,
    cachedBlacklist: ["/VerifiedEmails/", "/logo", "/Documents/", "/Achievements/Points", "/Achievements", "/Quizzes", "/Chats/Messages", "/CMS", "/Payments", "/Vouchers"],
    cookies: null,
    router: null,
};
export function setApiConfig(config) {
    apiConfig = { ...apiConfig, ...config };
}
function cacheSettingEnabled(key) {
    if (localStorage.getItem('showHiddenSettings') !== 'true')
        return true;
    return localStorage.getItem(key) !== 'false';
}
export function getApiConfig() {
    return {
        ...apiConfig,
        useCachedEndpoints: cacheSettingEnabled('useCachedEndpoints'),
    };
}
export function useApi() {
    function getFromCache(url) {
        if (!cacheSettingEnabled('useLocalCache'))
            return null;
        const raw = localStorage.getItem(url);
        if (!raw)
            return null;
        try {
            const cache = JSON.parse(raw);
            if (cache.expires > Date.now()) {
                return cache.data;
            }
        }
        catch {
            // ignore parse errors
        }
        return null;
    }
    // Read the server content hash (x-mgcache-md5) stored alongside a cached
    // response. Used to detect when a re-fetched payload differs from a baseline
    // without diffing hand-picked fields.
    function getCacheHash(url) {
        if (!cacheSettingEnabled('useLocalCache'))
            return null;
        const raw = localStorage.getItem(url);
        if (!raw)
            return null;
        try {
            const cache = JSON.parse(raw);
            if (cache.expires > Date.now()) {
                return cache.hash ?? null;
            }
        }
        catch {
            // ignore parse errors
        }
        return null;
    }
    function cache(url, data, ttlMins = 3, hash = null) {
        if (!cacheSettingEnabled('useLocalCache'))
            return;
        try {
            localStorage.setItem(url, JSON.stringify({
                data: data,
                expires: new Date().setMinutes(new Date().getMinutes() + ttlMins),
                hash: hash
            }));
        }
        catch (e) {
            console.log(e, 'Caching error (full?)');
            manageCacheUsage();
        }
        if (apiConfig.api.indexOf('sysodev') !== -1) {
            showCacheSize();
        }
    }
    function showCacheSize() {
        let total = 0;
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(apiConfig.api)) {
                const value = localStorage.getItem(key);
                if (value) {
                    total += new Blob([value]).size;
                }
            }
        }
        console.log(`Current cache size: ${(total / 1024).toFixed(2)} KB`);
    }
    function manageCacheUsage() {
        const expiredSize = clearExpiredCaches();
        console.log(`Cleared ${(expiredSize / 1024).toFixed(2)} KB of expired cache`);
        if (expiredSize < 1024 * 1536) { // if we cleared less than 1.5MB, also clear all cache to free up space
            console.log('Clearing all cache to free up more space (not settings)');
            clearCache(true);
        }
    }
    function clearCache(onlyCachedApiCalls = true) {
        if (!cacheSettingEnabled('useLocalCache'))
            return;
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && (key.startsWith(apiConfig.api) || !onlyCachedApiCalls)) {
                localStorage.removeItem(key);
            }
        }
    }
    function cacheUrl(path) {
        const useCachedEndpoints = cacheSettingEnabled('useCachedEndpoints');
        const useCachedPrefix = useCachedEndpoints && !apiConfig.cachedBlacklist.some(endpoint => path.includes(endpoint));
        return apiConfig.api + (useCachedPrefix ? '/Cached' : '') + path;
    }
    function clearExpiredCaches() {
        var totalCleared = 0;
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith(apiConfig.api)) {
                try {
                    const entry = JSON.parse(localStorage.getItem(key));
                    const byteLength = new Blob([localStorage.getItem(key)]).size;
                    if (entry && entry.expires !== undefined && entry.expires < Date.now()) {
                        localStorage.removeItem(key);
                        totalCleared += byteLength;
                    }
                }
                catch {
                    // ignore
                }
            }
        }
        return totalCleared;
    }
    function GET(url, onSuccess, useCache = false, forceSetCache = false, ttlMins = 3, retrying = false, staleWhileRevalidate = false, pollUntilChanged = false, pollChangeKey, pollIntervalMs = SWR_POLL_INTERVAL_MS) {
        url = url.replace(/\/+$/, '');
        const useCachedEndpoints = cacheSettingEnabled('useCachedEndpoints');
        if (useCachedEndpoints && !url.toLowerCase().includes('/cached/')) {
            if (!apiConfig.cachedBlacklist.some(endpoint => url.includes(endpoint))) {
                if (apiConfig.api && url.startsWith(apiConfig.api)) {
                    url = apiConfig.api + "/Cached" + url.slice(apiConfig.api.length);
                }
                else {
                    url = "/Cached" + url;
                }
            }
        }
        if (!url.includes(apiConfig.api)) {
            url = apiConfig.api + url;
        }
        // When staleWhileRevalidate is on, never short-circuit on cache — always
        // fall through to the SWR branch so the network call runs (and polling
        // can kick in if requested).
        if (useCache && !staleWhileRevalidate) {
            const fromCache = getFromCache(url);
            let attempts = 0;
            if (fromCache) {
                if (fromCache === 'loading') {
                    const interval = setInterval(() => {
                        const updatedCache = getFromCache(url);
                        if (updatedCache && updatedCache !== 'loading') {
                            clearInterval(interval);
                            if (onSuccess)
                                onSuccess(updatedCache);
                        }
                        attempts++;
                        if (attempts > 14) {
                            clearInterval(interval);
                            localStorage.removeItem(url);
                            GET(url, onSuccess, useCache, forceSetCache);
                        }
                    }, 500);
                    return;
                }
                if (onSuccess)
                    onSuccess(fromCache);
                return;
            }
        }
        // Comparison key for pollUntilChanged, used only as a fallback when the
        // server content hash (x-mgcache-md5) isn't available. Default: JSON of the
        // whole payload. Callers can pass a custom function that returns a stable
        // string built only from the fields they care about — otherwise volatile
        // fields (timestamps, running balances etc.) cause the poll to bail out
        // before the real change arrives on the server.
        const changeKey = pollChangeKey ?? ((d) => JSON.stringify(d));
        // Preferred change signal: the server's content hash for the stale cached
        // entry. It only changes when the cached payload is invalidated, so it
        // sidesteps the volatile-field churn the changeKey fallback exists to avoid.
        let stalePayloadString = null;
        let staleHash = null;
        if (staleWhileRevalidate) {
            const raw = localStorage.getItem(url);
            if (raw) {
                try {
                    const parsed = JSON.parse(raw);
                    const staleData = parsed.data;
                    if (staleData && staleData !== 'loading') {
                        if (onSuccess)
                            onSuccess(staleData, /* isStale */ true);
                        stalePayloadString = changeKey(staleData);
                        staleHash = parsed.hash ?? null;
                    }
                }
                catch { /* ignore */ }
            }
        }
        const previousCache = getFromCache(url);
        if (forceSetCache) {
            cache(url, 'loading', ttlMins);
        }
        // When the caller has just written and expects the response to change,
        // keep refetching at an interval until the server payload differs from
        // the initially-cached snapshot, or we hit the attempt cap.
        const enablePolling = pollUntilChanged && staleWhileRevalidate && stalePayloadString !== null;
        const runFetch = (attemptsRemaining) => {
            httpInstance.get(url)
                .then((result) => {
                if (enablePolling && attemptsRemaining > 0) {
                    const freshHash = result.headers['x-mgcache-md5'] || null;
                    // Prefer the server content hash when both sides have one;
                    // otherwise fall back to the payload-derived changeKey.
                    const unchanged = (staleHash && freshHash)
                        ? freshHash === staleHash
                        : changeKey(result.data) === stalePayloadString;
                    // Dev-only visibility into the SWR poll: which url we're
                    // polling, the stale baseline hash, and the fresh hash.
                    if (apiConfig.api.indexOf('sysodev') !== -1) {
                        if (unchanged) {
                            console.log(`[SWR poll] still stale — url: ${url}, staleHash: ${staleHash}, freshHash: ${freshHash}, attemptsRemaining: ${attemptsRemaining}`);
                        }
                        else {
                            console.log(`[SWR poll] changed — url: ${url}, staleHash: ${staleHash}, newHash: ${freshHash}`);
                        }
                    }
                    if (unchanged) {
                        setTimeout(() => runFetch(attemptsRemaining - 1), pollIntervalMs);
                        return;
                    }
                }
                if (useCache || forceSetCache || staleWhileRevalidate) {
                    cache(url, result.data, ttlMins, result.headers['x-mgcache-md5'] || null);
                }
                if (onSuccess)
                    onSuccess(result.data, /* isStale */ false);
            })
                .catch((result) => {
                if (result.response && result.response.status == 401 && !retrying) {
                    refreshLogin(() => {
                        GET(url, onSuccess, useCache, forceSetCache, ttlMins, true, staleWhileRevalidate, pollUntilChanged, pollChangeKey, pollIntervalMs);
                    });
                }
                else if (result.response && result.response.status == 401 && retrying) {
                    console.error('Retrying GET request failed due to unauthorized access:', url);
                    if (forceSetCache)
                        localStorage.removeItem(url);
                }
                else if (enablePolling && attemptsRemaining > 0) {
                    setTimeout(() => runFetch(attemptsRemaining - 1), pollIntervalMs);
                }
                else if (staleWhileRevalidate) {
                    // Silently swallow — user keeps seeing stale/cached data
                }
                else if (previousCache && previousCache !== 'loading') {
                    cache(url, previousCache, ttlMins);
                    if (onSuccess)
                        onSuccess(previousCache, /* isStale */ true);
                }
                else if (forceSetCache) {
                    localStorage.removeItem(url);
                }
            });
        };
        runFetch(enablePolling ? SWR_POLL_MAX_ATTEMPTS - 1 : 0);
    }
    function login(email, password, onSuccess = null) {
        httpInstance.post(apiConfig.api + "/Sessions", {
            "userName": email,
            "password": password
        })
            .then((result) => {
            try {
                const token = result.data.session.token;
                apiConfig.cookies.set('token', token, '30min');
                httpInstance.defaults.headers.common = { "Authorization": "bearer " + token };
                if (onSuccess)
                    onSuccess(true);
            }
            catch {
                if (onSuccess)
                    onSuccess(false);
            }
        });
    }
    function refreshLogin(onSuccess = null) {
        if (!apiConfig.cookies.get('user') || !apiConfig.cookies.get('pass')) {
            window.location.href = "/MG_POP.html";
            return;
        }
        const email = apiConfig.cookies.get('user');
        const password = apiConfig.cookies.get('pass');
        login(email, password, onSuccess);
    }
    function openV1(path, returnPath = null) {
        if (!returnPath)
            returnPath = '#';
        window.location.href = "/MG_NAV_" + path + "_";
        setTimeout(() => {
            window.location.href = window.location.origin + '/#/' + returnPath;
        }, 500);
    }
    function signalV1(op, val, val2, val3, returnPath = null) {
        if (!returnPath)
            returnPath = '#';
        window.location.href = "/MG_SIG_" + op + "_" + val + "_" + val2 + "_" + val3 + "_";
        setTimeout(() => {
            window.location.href = window.location.origin + '/#/' + returnPath;
        }, 500);
    }
    return {
        GET,
        getFromCache,
        getCacheHash,
        cache,
        cacheUrl,
        clearExpiredCaches,
        login,
        refreshLogin,
        openV1,
        signalV1,
    };
}
